import merge from 'lodash.merge';
import { useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import YesNoDialog from '../../components/dialogs/yes-no-dialog';
import Toolbar from '../../components/page-toolbar';
import { PrivatePage } from '../../components/private-page';
import { FeedbackContentFullEditor } from '../../features/feedback/feedback-content-full-editor';
import { GetLanguagesAsync } from '../../services/country';
import {
  getPendingDocumentContentByFeedbackIdAsync,
  getPendingDocumentLatestAsync,
  getPendingFeedbackByIdAsync,
  patchFeedbackAsAdminAsync
} from '../../services/feedback';
import { appStore } from '../../stores/app-store';
import { YesNoDialogResult } from '../../types/dialog-result';
import { Feedback, FeedbackApproval } from '../../types/feedback';
import { useToast } from '../../contexts/toast-context';

const FeedbackDetailView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [entity, setEntity] = useState<Feedback>();
  const [languages, setLanguages] = useState<string[]>([]);
  const [, setSelectedTab] = useState<string>('');
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<string>('');
  const [confirmMessage, setConfirmMessage] = useState<string>();
  const [confirmTitle, setConfirmTitle] = useState<string>();
  const { onToastOpen } = useToast();
  const detailFeedbackBreadcrumb = [
    { label: 'Dashboard', href: 'dashboard' },
    { label: 'Administration', href: '../administration' },
    {
      label: 'Feedback Pending Approval',
      href: 'administration/feedbacks/pending'
    },
    {
      label: 'Feedback Pending Approval Details',
      href: `administration/feedback-detail-view/${id}`
    }
  ];

  const getDetails = async () => {
    try {
      appStore.showLoading();

      // combine feedback and feedback documents (resources)
      const feed1 = await getPendingFeedbackByIdAsync(parseInt(id!));
      const latestDocument = await getPendingDocumentLatestAsync(parseInt(id!));
      const feed2 = await getPendingDocumentContentByFeedbackIdAsync(
        parseInt(id!),
        latestDocument.id
      );

      const feedback = merge(feed1, feed2);

      setEntity(feedback);
    } catch (ex) {
      console.error(ex);
    } finally {
      appStore.hideLoading();
    }
  };

  const getLanguages = async () => {
    const response = await GetLanguagesAsync();
    const languages: string[] = [];
    response.data.forEach((f) => {
      if (!languages.some((c) => c === f.language)) {
        languages.push(f.language!);
      }
    });
    setLanguages(languages);
  };

  const showApproveSectionDialog = (approval: 'Approved' | 'Rejected') => {
    setApprovalStatus(approval);
    if (approval === 'Approved') {
      setConfirmTitle('Approve feedback');
      setConfirmMessage('Are you sure you want to approve this feedback?');
    } else {
      setConfirmTitle('Reject feedback');

      setConfirmMessage('Are you sure you want to reject this feedback?');
    }
    setOpenApprovalDialog(true);
  };

  const confirmApproveSectionDialog = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        let document = await getPendingDocumentLatestAsync(parseInt(id!));
        let request: FeedbackApproval = {
          fileName: document?.fileName ?? '',
          version: document?.version ?? '',
          status: approvalStatus,
          isGlobal: true,
          isDeleted: false
        };

        let result = await patchFeedbackAsAdminAsync(
          parseInt(id!),
          document.id,
          request
        );
        if (entity) {
          let newEntity = { ...entity };
          if (newEntity) {
            newEntity.status = result.status;
            setEntity(newEntity!);
          }
        }

        if (approvalStatus === 'Approved') {
          onToastOpen({
            title: 'Feedback approval',
            body: <div>{`Feedback approved.`}.</div>,
            style: 'success'
          });
        }

        navigate(`../feedbacks/pending`);
      } catch (e: any) {
        onToastOpen({
          title: 'Feedback approval',
          body: `Feedback cannot be approved.`,
          style: 'error'
        });
      }
    }
    setOpenApprovalDialog(false);
  };
  const toggleLanguageTab = (activeTab: string) => {
    if (activeTab === 'language') {
      setSelectedTab(activeTab);
    } else {
      setSelectedTab('');
    }
  };

  useEffect(() => {
    if (id) {
      getDetails();
    }
    getLanguages();
  }, []);

  return (
    <PrivatePage breadcrumb={detailFeedbackBreadcrumb} pageTitle={'Feedback'}>
      <>
        <Toolbar title={id ? (entity?.name ?? '') : 'New Feedback'}></Toolbar>
        <hr />
        <Container>
          <Row>
            <Col>
              {entity && (
                <FeedbackContentFullEditor
                  languages={languages}
                  entity={entity}
                  isViewMode={true}
                  toggleLanguageTab={toggleLanguageTab}
                />
              )}
            </Col>
          </Row>
          <Row className={'mt-3'}>
            <Col md={6}>
              <Button
                variant={'danger'}
                className={'w-100 mt-1'}
                onClick={() => showApproveSectionDialog('Rejected')}>
                Reject
              </Button>
            </Col>
            <Col md={6}>
              <Button
                variant={'success'}
                className={'w-100 mt-1'}
                onClick={() => showApproveSectionDialog('Approved')}>
                Approve
              </Button>
            </Col>
          </Row>
        </Container>
      </>
      {openApprovalDialog && (
        <YesNoDialog
          title={confirmTitle}
          onConfirm={confirmApproveSectionDialog}
          onClose={() => setOpenApprovalDialog(false)}>
          {confirmMessage}
        </YesNoDialog>
      )}
    </PrivatePage>
  );
};

export default FeedbackDetailView;
